import { Language } from '@vae/server/dist/client'
import dayjs from 'dayjs'
import { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ObjectContext } from '../../../contexts/objectContext/ObjectContext'
import i18n from '../../../i18n'
import { trpc } from '../../../modules/trpc'
import { groupEventsByDate } from '../utils/events/groupEventsByDate'

export type EventsHook = ReturnType<typeof useEvents>

export const useEvents = () => {
	const { id: objectId } = useContext(ObjectContext)
	const { t } = useTranslation('common')

	const startDate = dayjs().startOf('day').toISOString()

	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isInitialLoading } =
		trpc.achievements.getSavingEventsPaginated.useInfiniteQuery(
			{
				objectId,
				startDate,
				limit: 7,
			},
			{
				enabled: !!objectId,
				getNextPageParam: lastPage => lastPage.result?.nextCursor ?? null,
				staleTime: 5 * 60 * 1000,
			}
		)

	const flattened = useMemo(() => {
		if (!data?.pages) return []
		return data.pages.flatMap(page => page.result?.events ?? [])
	}, [data])

	const events = useMemo(
		() =>
			groupEventsByDate(
				flattened.map(event => ({
					...event,
					date: new Date(event.date),
					createdAt: new Date(event.createdAt),
					updatedAt: new Date(event.updatedAt),
					weekStart: event.weekStart ? new Date(event.weekStart) : null,
				})),
				i18n.language as Language,
				t
			),
		[flattened, t]
	)

	const hasEvents = useMemo(() => {
		return flattened.length > 0
	}, [flattened.length])

	return {
		events,
		fetchNextPage,
		hasNextPage: hasNextPage ?? false,
		isFetchingNextPage,
		isInitialLoading,
		hasEvents,
	}
}
